import { Component } from 'react';
import { connect } from 'react-redux';
import memoize from 'transmute/memoize';
import getIn from 'immutable-less/transmute/getIn';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import DirectedGraph from 'automation-ui-directed-graph/DirectedGraph';
import calculateNodePositions from 'automation-ui-directed-graph/utils/calculateNodePositions';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import { getIsEmbedded } from 'automation-ui-canvas/selectors/embedSelectors';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { getPortalId } from 'automation-ui-canvas/selectors/authSelectors';
import { CARD_WIDTH_PADDING, LEFT_CANVAS_PADDING } from 'automation-ui-flow-core/canvas/graphConstants';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { withFeaturePermissions } from 'automation-ui-permissions/feature-permissions/decorators/FeaturePermissionsDecorator';
import { getAllEndMarkersData } from 'automation-ui-canvas/utils/endMarkerFlowUtils';
import { getFlowValidation } from 'automation-ui-canvas/selectors/flowSelectors';
import { getPreviousActionId } from 'automation-ui-action-features/records/recordsUtils';
import { getNodeHeights } from 'automation-ui-canvas/utils/getNodeHeights';
import withFlpAccess from 'automation-ui-permissions/flp/hocs/withFlpAccess';
import { clearScrollToActionCardState } from 'automation-ui-canvas/utils/routingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
class UIFlowCanvas extends Component {
  constructor(...args) {
    super(...args);
    this.getDirectedGraph = memoize(() => {
      const {
        flow,
        goToHoverNode: {
          fromNode,
          toNode,
          isHover
        },
        nodeWidth,
        nodeHeight,
        showEnrollmentCard
      } = this.props;
      const directedGraph = translateToGraph(flow, false, nodeWidth, nodeHeight, showEnrollmentCard);
      const existingGoTo = fromNode && directedGraph.get('goToPaths')[fromNode] || {};
      const newGoToPaths = Object.assign({}, directedGraph.get('goToPaths'));
      if (fromNode && toNode && flow.getActionById(existingGoTo ? getPreviousActionId(fromNode) : fromNode) !== undefined) {
        newGoToPaths[fromNode] = Object.assign({}, existingGoTo, {
          toNodeId: toNode,
          isHover
        });
      }
      const goToActionEdgeId = this.props.goToEditNodeId;
      if (newGoToPaths[goToActionEdgeId]) {
        newGoToPaths[goToActionEdgeId] = Object.assign({}, newGoToPaths[goToActionEdgeId], {
          isGoToEditNode: true
        });
      }
      return directedGraph.set('goToPaths', newGoToPaths);
    }, () => {
      const {
        flow,
        goToHoverNode: {
          fromNode,
          toNode,
          isHover
        }
      } = this.props;
      return `${uniqueIdForObject(flow)}-${fromNode}-${toNode}-${isHover}`;
    });
    this.getEndMarkersFromEdges = memoize((flow, directedGraph) => {
      const endMarkerNodes = getAllEndMarkersData({
        flow,
        directedGraph
      });
      return endMarkerNodes;
    }, (flow, directedGraph) => `${uniqueIdForObject(flow)}-${uniqueIdForObject(directedGraph)}`);
    this.onRenderEndMarker = edgeId => {
      const {
        flow,
        onRenderEndMarker
      } = this.props;
      const directedGraph = this.getDirectedGraph();
      const endMarkersDataFromEdges = this.getEndMarkersFromEdges(flow, directedGraph);
      return onRenderEndMarker({
        edgeId,
        isMultiEndMarkerWorkflow: endMarkersDataFromEdges.size > 1
      });
    };
    this.onRenderConnectorButton = props => {
      const {
        onRenderConnectorButton
      } = this.props;
      if (onRenderConnectorButton) {
        return onRenderConnectorButton(props);
      }
      return null;
    };
  }
  componentDidUpdate() {
    const {
      scrollToActionOptions,
      scrollElementRef = {
        current: null
      },
      zoomLevel,
      nodeWidth,
      nodeHeight
    } = this.props;
    const scrollElement = scrollElementRef && scrollElementRef.current;
    if (!scrollToActionOptions || !scrollElement) {
      return;
    }
    const {
      actionId,
      scrollBehavior = 'smooth'
    } = scrollToActionOptions;
    const directedGraph = this.getDirectedGraph();
    const nodeHeights = this.getNodeHeights();
    const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
    const estimatedNodePosition = nodePositions.get(actionId);
    if (estimatedNodePosition) {
      const {
        width,
        height
      } = scrollElement.getBoundingClientRect();
      const scrollTop = estimatedNodePosition.y + nodeHeight / zoomLevel / 2;
      const scrollLeft = estimatedNodePosition.x + LEFT_CANVAS_PADDING + nodeWidth / 2 + CARD_WIDTH_PADDING;

      // Depending on what changes were involved in the render, scrollTo() may not activate if we ask immediately
      window.requestAnimationFrame(() => {
        scrollElement.scrollTo({
          behavior: scrollBehavior,
          top: scrollTop * zoomLevel - height / 2,
          left: scrollLeft * zoomLevel - width / 2
        });
      });
      clearScrollToActionCardState(this.props.history);
    }
  }
  getNodeHeights() {
    const {
      showEnrollmentCard
    } = this.props;
    return getNodeHeights({
      showEnrollmentCard
    });
  }
  render() {
    const {
      flow,
      onRenderAction,
      onRenderBranchName,
      highlightedActionIds,
      isEmbedded,
      showOuterPadding,
      firstNodeRef,
      setFirstNodeRef,
      scrollElementRef,
      isMulti,
      readOnlyOverride,
      TopContent,
      disableVirtualRendering,
      showEdgeTooltips,
      zoomLevel,
      getFeaturePermission,
      nodeWidth,
      nodeHeight,
      highlightedEdgeThickness,
      highlightedEdgeColor,
      getIsBranchEdgeHighlighted,
      flpAccessLoading,
      flpAccessError,
      flpAccessData
    } = this.props;
    const directedGraph = this.getDirectedGraph();
    const hasFlpWriteAccess = !flpAccessLoading && !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ACTIONS));
    const {
      hasFeatureAccess: hasEditActionAccess
    } = getFeaturePermission({
      feature: EDIT_ACTIONS
    });
    return /*#__PURE__*/_jsx(DirectedGraph, {
      onRenderNode: onRenderAction,
      onRenderBranchName: onRenderBranchName,
      onRenderConnectorButton: this.onRenderConnectorButton,
      onRenderEndMarker: this.onRenderEndMarker,
      scrollElementRef: scrollElementRef || {},
      setFirstNodeRef: ref => {
        firstNodeRef.current = ref;
        setFirstNodeRef();
      },
      onNodeHeightsChanged: () => {},
      highlightedNodeIds: highlightedActionIds,
      directedGraph: directedGraph,
      isMulti: isMulti,
      isEmbedded: isEmbedded,
      graphId: flow.getFlowId(),
      showOuterPadding: showOuterPadding && !isEmbedded,
      TopContent: TopContent,
      readOnly: !hasEditActionAccess || !hasFlpWriteAccess || readOnlyOverride,
      disableVirtualRendering: disableVirtualRendering,
      zoomLevel: zoomLevel,
      showEdgeTooltips: showEdgeTooltips,
      nodeWidth: nodeWidth,
      nodeHeight: nodeHeight,
      nodeHeights: this.getNodeHeights(),
      highlightedEdgeThickness: highlightedEdgeThickness,
      highlightedEdgeColor: highlightedEdgeColor,
      getIsBranchEdgeHighlighted: getIsBranchEdgeHighlighted
    });
  }
}
UIFlowCanvas.defaultProps = {
  goToHoverNode: {},
  showOuterPadding: true,
  isMulti: false,
  readOnlyOverride: false,
  disableVirtualRendering: false
};
const mapStateToProps = (state, {
  flow,
  location
}) => {
  const flowId = flow.getFlowId();
  return {
    isEmbedded: getIsEmbedded(state),
    zoomLevel: getZoomLevel(flowId)(state),
    portalId: getPortalId(state),
    flowValidation: getFlowValidation(flowId)(state),
    flowId,
    // history and location are mutable objects, we need this to update the
    // component when the state changes
    scrollToActionOptions: getIn(['state', 'scrollToAction'], location)
  };
};
export default compose(withRouter, connect(mapStateToProps), withFeaturePermissions, withFlpAccess, wireAuthConsumer)(UIFlowCanvas);